// 消えない記録 (セーブ2層化の下側・2026-07-29)。
// 設計の正本 = `docs/candidates/2026-07-29-achievement-flavor-text-design.md` §8。
//
// なぜ要るか (§8 の問題2点):
//   1. 終幕を見終わると進行を丸ごと消していた = 総プレイ時間や死亡数を見せた直後に、その記録ごと失う。
//   2. ボスを1体足す/並べ替えると `rosterIds` 判定でセーブが丸ごと破棄される
//      = 常打ちの刑吏を足した時点で、配布中のセーブが全部消える。
//
// 直し方: 「周回ごとに戻すもの」と「絶対に消さないもの」を別キー・別スキーマに分ける。
//   周回層 = CampaignState (`persistence.ts` の save:v3)。ボス列の指紋つき・周回で戻る。
//   この層 = record:v1。**ボス列の指紋を持たない** = ボスを足しても消えない。
//   知らないボスidが混じっても無視するだけでよい (実績のほうが本編より長生きするので最初から緩くする)。
//
// 累計の数え方 (二重計上を避ける):
//   carried = 完了した周回ぶんの合計。 進行中の周は CampaignState 側が持っている。
//   見せる累計 = carried + 現在の周 (`lifetimeTotals`)。 周が終わった時だけ carried へ畳む
//   (`foldRunIntoRecord`)。 途中でやめても現在の周のぶんは周回層に残るので、どちらでも数が合う。
//   タイトルの「最初から」で周回層を消す時は `foldAbandonedRunIntoRecord` で累計だけ先に畳む (2026-10-19)。

import type { CampaignState, EndingChoice } from './types'

export type RunTotals = {
  deaths: number
  timeSec: number
  parrySuccess: number
  parryFail: number
  dodge: number
  counter: number
  // 2026-07-30 追加。 justParry は parrySuccess の部分集合 (足し合わせない)。 guard は独立。
  //   旧 record:v1 には無いので loadRecord の sanitizeTotals が 0 で埋める (version 据え置き)。
  justParry: number
  guard: number
}

export type PersistentRecord = {
  version: 1
  // 終幕まで見終えた回数。 周回層をリセットするたびに +1。
  runsCompleted: number
  // 見た終幕の種類 (重複なし)。
  endingsSeen: EndingChoice[]
  // これまでに一度でも撃破したボス (周回をまたぐ)。 周回層の defeatedBossIds とは別物 =
  //   あちらは「この周で倒したか」でボス進行の判定に使う。 混ぜると次の周が全部撃破済みで始まる。
  clearedBossIdsEver: string[]
  // 完了した周回ぶんの累計 (進行中の周は含まない)。
  carried: RunTotals
  // ボス別の累計 (完了した周回ぶん)。 知らないidは読み飛ばす。
  carriedByBossId: Record<string, RunTotals>
  // 解放した実績id。 ⚠️ 2026-07-29 時点で発行側はまだ無い (実績の中身は同文書 §6 で設計中)。
  //   ここに置くのは、実績が入る時にセーブをもう一度作り直さずに済ませるため。
  achievements: string[]
}

export function createRecord(): PersistentRecord {
  return {
    version: 1,
    runsCompleted: 0,
    endingsSeen: [],
    clearedBossIdsEver: [],
    carried: emptyTotals(),
    carriedByBossId: {},
    achievements: [],
  }
}

export function emptyTotals(): RunTotals {
  return { deaths: 0, timeSec: 0, parrySuccess: 0, parryFail: 0, dodge: 0, counter: 0, justParry: 0, guard: 0 }
}

function addTotals(a: RunTotals, b: RunTotals): RunTotals {
  return {
    deaths: a.deaths + b.deaths,
    timeSec: a.timeSec + b.timeSec,
    parrySuccess: a.parrySuccess + b.parrySuccess,
    parryFail: a.parryFail + b.parryFail,
    dodge: a.dodge + b.dodge,
    counter: a.counter + b.counter,
    justParry: a.justParry + b.justParry,
    guard: a.guard + b.guard,
  }
}

function sumValues(m: Record<string, number> | undefined): number {
  return Object.values(m ?? {}).reduce((s, v) => s + (Number.isFinite(v) ? v : 0), 0)
}

// 進行中の周の合計 (CampaignState から読む)。 旧セーブに無いフィールドは ?? でガード。
export function runTotals(c: CampaignState): RunTotals {
  return {
    deaths: c.totalDeaths ?? 0,
    timeSec: c.totalTime ?? 0,
    parrySuccess: sumValues(c.parrySuccessByBossId),
    parryFail: sumValues(c.parryFailByBossId),
    dodge: sumValues(c.dodgeByBossId),
    counter: sumValues(c.counterByBossId),
    justParry: sumValues(c.justParryByBossId),
    guard: sumValues(c.guardByBossId),
  }
}

// ボス別・進行中の周のぶん。
export function runTotalsByBoss(c: CampaignState): Record<string, RunTotals> {
  const ids = new Set<string>([
    ...Object.keys(c.deathsByBossId ?? {}),
    ...Object.keys(c.timeByBossId ?? {}),
    ...Object.keys(c.parrySuccessByBossId ?? {}),
    ...Object.keys(c.parryFailByBossId ?? {}),
    ...Object.keys(c.dodgeByBossId ?? {}),
    ...Object.keys(c.counterByBossId ?? {}),
    ...Object.keys(c.justParryByBossId ?? {}),
    ...Object.keys(c.guardByBossId ?? {}),
  ])
  const out: Record<string, RunTotals> = {}
  for (const id of ids) {
    out[id] = {
      deaths: c.deathsByBossId?.[id] ?? 0,
      timeSec: c.timeByBossId?.[id] ?? 0,
      parrySuccess: c.parrySuccessByBossId?.[id] ?? 0,
      parryFail: c.parryFailByBossId?.[id] ?? 0,
      dodge: c.dodgeByBossId?.[id] ?? 0,
      counter: c.counterByBossId?.[id] ?? 0,
      justParry: c.justParryByBossId?.[id] ?? 0,
      guard: c.guardByBossId?.[id] ?? 0,
    }
  }
  return out
}

// 画面に見せる累計 = 完了した周 + 進行中の周。
export function lifetimeTotals(rec: PersistentRecord, c: CampaignState): RunTotals {
  return addTotals(rec.carried, runTotals(c))
}

export function lifetimeTotalsForBoss(rec: PersistentRecord, c: CampaignState, bossId: string): RunTotals {
  const cur = runTotalsByBoss(c)[bossId] ?? emptyTotals()
  return addTotals(rec.carriedByBossId[bossId] ?? emptyTotals(), cur)
}

// 撃破を「一度でも倒した」側へ刻む (周回をまたいで残る)。 同じidは増やさない。
export function markBossClearedEver(rec: PersistentRecord, bossId: string): PersistentRecord {
  if (rec.clearedBossIdsEver.includes(bossId)) return rec
  return { ...rec, clearedBossIdsEver: [...rec.clearedBossIdsEver, bossId] }
}

export function markEndingSeen(rec: PersistentRecord, kind: EndingChoice): PersistentRecord {
  if (rec.endingsSeen.includes(kind)) return rec
  return { ...rec, endingsSeen: [...rec.endingsSeen, kind] }
}

// 1周が終わった時: 進行中の周のぶんを carried へ畳み、周回数を +1 する。
//   呼ぶのは「周回層をリセットする直前」の1箇所だけ (game.ts の終幕完走)。
//   2回呼ぶと二重計上になるので、リセットと必ず対にする。
export function foldRunIntoRecord(rec: PersistentRecord, c: CampaignState): PersistentRecord {
  return { ...foldTotals(rec, c), runsCompleted: rec.runsCompleted + 1 }
}

// タイトルの「最初から」で、 終幕まで行かずに周回層を消す時 (2026-10-19)。
//   死亡数・時間・パリィ等の累計だけ carried へ畳み、 周回数 (runsCompleted) は増やさない
//   = 千秋楽の扉 (isSenshurakuOpen) を「途中でやめた」だけで開けない。
//   foldRunIntoRecord と同じく、 周回層のリセット (resetCampaign) と必ず対にする。
export function foldAbandonedRunIntoRecord(rec: PersistentRecord, c: CampaignState): PersistentRecord {
  return foldTotals(rec, c)
}

function foldTotals(rec: PersistentRecord, c: CampaignState): PersistentRecord {
  const byBoss = runTotalsByBoss(c)
  const carriedByBossId = { ...rec.carriedByBossId }
  for (const [id, t] of Object.entries(byBoss)) {
    carriedByBossId[id] = addTotals(carriedByBossId[id] ?? emptyTotals(), t)
  }
  return {
    ...rec,
    carried: addTotals(rec.carried, runTotals(c)),
    carriedByBossId,
  }
}
